// src/views/CompanyCalendar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Split from 'react-split';
import './CompanyCalendar.css';
import { FaPlus, FaTrash, FaCalendarAlt, FaStickyNote, FaEdit, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;

function getCurrentUserMeta() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    return {
      id: Number(u.userid || 0) || null,
      name: u.fullname || u.fullName || u.name || u.login || '',
      isAdmin: (() => {
        const flag = u.isadmin ?? u.isAdmin ?? u.admin ?? u.role ?? u.roleid;
        const norm = typeof flag === 'string' ? flag.trim().toLowerCase() : flag;
        if (norm === true) return true;
        if (typeof norm === 'number') return norm === 1;
        if (typeof norm === 'string') {
          return norm === '1' || norm === 'true' || norm === 'admin';
        }
        return false;
      })(),
    };
  } catch {
    return { id: null, name: '', isAdmin: false };
  }
}

export default function CompanyCalendar() {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [monthSplitSizes, setMonthSplitSizes] = useState(() => {
    try {
      const raw = localStorage.getItem('calendar.monthSplitSizes');
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
        return parsed;
      }
    } catch {}
    return [65, 35];
  });
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'list'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState('');
  const [time, setTime] = useState('');
  const [editTime, setEditTime] = useState('');
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const currentUser = useMemo(() => getCurrentUserMeta(), []);
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const filterMonth = useMemo(() => {
    const d = monthCursor instanceof Date ? monthCursor : new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [monthCursor]);
  const currentYear = useMemo(() => monthCursor.getFullYear(), [monthCursor]);
  const currentMonthName = useMemo(() => monthNames[monthCursor.getMonth()], [monthCursor]);

  const changeMonthBy = (delta) => {
    setMonthCursor(prev => {
      const base = prev instanceof Date ? prev : new Date();
      const d = new Date(base.getFullYear(), base.getMonth(), 1);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  const changeYearBy = (delta) => {
    setMonthCursor(prev => {
      const base = prev instanceof Date ? prev : new Date();
      const d = new Date(base.getFullYear(), base.getMonth(), 1);
      d.setFullYear(d.getFullYear() + delta);
      return d;
    });
  };

  // Keep selectedDate aligned with the visible month; on first load this will be "today"
  useEffect(() => {
    if (!selectedDate || !String(selectedDate).startsWith(filterMonth)) {
      setSelectedDate(prev => {
        if (prev && String(prev).slice(0, 7) === filterMonth) return prev;
        return `${filterMonth}-01`;
      });
    }
  }, [filterMonth, selectedDate]);

  const loadEvents = async (month) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (month) params.set('month', month.slice(0, 7));
      const res = await fetch(`${API}/api/calendar/events?${params.toString()}`);
      const j = await res.json().catch(() => ({ data: [] }));
      if (!res.ok) throw new Error(j?.error || 'Failed to load events');
      const rows = Array.isArray(j.data) ? j.data : [];
      const mapped = rows.map(r => ({
        id: r.id,
        date: r.start_date ? String(r.start_date).slice(0, 10) : '',
        time: r.start_time ? String(r.start_time).slice(0, 5) : '',
        title: r.title || '',
        notes: r.notes || '',
        createdAt: r.created_at || null,
        createdBy: r.created_by_name || '',
        createdById: r.created_by || null,
      }));
      setEvents(mapped);
    } catch (e) {
      setError(e?.message || 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(filterMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth]);

  const eventsByDate = useMemo(() => {
    const month = (filterMonth || '').slice(0, 7); // YYYY-MM
    const out = {};
    (events || []).forEach(e => {
      if (!e.date) return;
      if (month && !String(e.date).startsWith(month)) return;
      const key = e.date;
      if (!out[key]) out[key] = [];
      out[key].push(e);
    });
    return out;
  }, [events, filterMonth]);

  const groupedByDate = useMemo(() => {
    const out = {};
    Object.entries(eventsByDate).forEach(([key, list]) => { out[key] = list; });
    const entries = Object.entries(out).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return entries;
  }, [eventsByDate]);

  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    const list = eventsByDate[selectedDate] || [];
    return list.slice().sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  }, [eventsByDate, selectedDate]);

  const addEvent = (e) => {
    e.preventDefault();
    const trimmedTitle = (title || '').trim();
    const trimmedNotes = (notes || '').trim();
    const trimmedTime = (time || '').slice(0, 5);
    if (!date || !trimmedTitle) return;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const body = { title: trimmedTitle, notes: trimmedNotes, date, time: trimmedTime || null };
        const res = await fetch(`${API}/api/calendar/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j?.error || j?.success === false) {
          throw new Error(j?.error || j?.message || 'Failed to create event');
        }
        const r = j.event || {};
        const ev = {
          id: r.id,
          date: r.start_date ? String(r.start_date).slice(0, 10) : date,
          time: r.start_time ? String(r.start_time).slice(0, 5) : trimmedTime,
          title: r.title || trimmedTitle,
          notes: r.notes || trimmedNotes,
          createdAt: r.created_at || new Date().toISOString(),
          createdBy: r.created_by_name || currentUser.name,
          createdById: r.created_by || currentUser.id,
        };
        setEvents(prev => [...prev, ev]);
        setTitle('');
        setNotes('');
        setTime('');
      } catch (err) {
        setError(err?.message || 'Failed to create event');
      } finally {
        setLoading(false);
      }
    })();
  };

  const deleteEvent = (id) => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API}/api/calendar/events/${id}`, { method: 'DELETE' });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j?.error || j?.success === false) {
          throw new Error(j?.error || j?.message || 'Failed to delete event');
        }
        setEvents(prev => prev.filter(e => e.id !== id));
      } catch (err) {
        setError(err?.message || 'Failed to delete event');
      } finally {
        setLoading(false);
      }
    })();
  };

  const beginEdit = (ev) => {
    setEditingId(ev.id);
    setEditTitle(ev.title || '');
    setEditNotes(ev.notes || '');
    setEditDate(ev.date || '');
    setEditTime(ev.time || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditNotes('');
    setEditDate('');
    setEditTime('');
  };

  const openEditFromMonth = (ev) => {
    if (!ev) return;
    setViewMode('list');
    beginEdit(ev);
  };

  const saveEdit = (id) => {
    const trimmedTitle = (editTitle || '').trim();
    const trimmedNotes = (editNotes || '').trim();
    const dateVal = (editDate || '').slice(0, 10);
    const timeVal = (editTime || '').slice(0, 5);
    if (!id || !trimmedTitle || !dateVal) return;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const body = { title: trimmedTitle, notes: trimmedNotes, start_date: dateVal, start_time: timeVal || null };
        const res = await fetch(`${API}/api/calendar/events/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j?.error || j?.success === false) {
          throw new Error(j?.error || j?.message || 'Failed to update event');
        }
        const r = j.event || {};
        setEvents(prev => prev.map(ev => {
          if (ev.id !== id) return ev;
          return {
            ...ev,
            title: r.title || trimmedTitle,
            notes: r.notes || trimmedNotes,
            date: r.start_date ? String(r.start_date).slice(0, 10) : dateVal,
            time: r.start_time ? String(r.start_time).slice(0, 5) : timeVal || ev.time || '',
            createdBy: r.created_by_name || ev.createdBy,
            createdById: r.created_by || ev.createdById,
            createdAt: r.created_at || ev.createdAt,
          };
        }));
        cancelEdit();
      } catch (err) {
        setError(err?.message || 'Failed to update event');
      } finally {
        setLoading(false);
      }
    })();
  };

  const positionTooltip = (evt) => {
    try {
      const clientX = evt.clientX;
      const clientY = evt.clientY;
      if (typeof window === 'undefined' || clientX == null || clientY == null) {
        setTooltipPos({ x: 0, y: 0 });
        return;
      }
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      const TW = 260;
      const TH = 180;
      let x = clientX + 16;
      let y = clientY + 16;
      if (x + TW > vw) x = Math.max(8, vw - TW - 8);
      if (y + TH > vh) y = Math.max(8, vh - TH - 8);
      setTooltipPos({ x, y });
    } catch {
      setTooltipPos({ x: 0, y: 0 });
    }
  };

  const handleEventHoverIn = (ev, evt) => {
    if (!ev) return;
    positionTooltip(evt);
    setHoveredEvent(ev);
  };

  const handleEventHoverMove = (ev, evt) => {
    if (!ev || !hoveredEvent || hoveredEvent.id !== ev.id) return;
    positionTooltip(evt);
  };

  const handleEventHoverOut = () => {
    setHoveredEvent(null);
  };

  const renderMonthGrid = () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStr = (filterMonth || '').slice(0, 7);
    if (!monthStr) return null;
    const [yStr, mStr] = monthStr.split('-');
    const year = Number(yStr);
    const monthIndex = Number(mStr) - 1; // 0-based
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;

    const first = new Date(year, monthIndex, 1);
    const firstDow = first.getDay(); // 0=Sun..6=Sat
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = String(day).padStart(2, '0');
      const dateKey = `${monthStr}-${dayStr}`;
      const dayEvents = (eventsByDate[dateKey] || []).slice().sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      cells.push({ day, dateKey, events: dayEvents });
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }

    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="cc-month-grid">
        {dow.map(d => (
          <div key={d} className="cc-month-dow">
            {d}
          </div>
        ))}
        {weeks.flatMap((week, wi) =>
          week.map((cell, ci) => {
            const key = `${wi}-${ci}`;
            if (!cell) {
              return <div key={key} className="cc-month-cell empty" />;
            }
            const isToday = cell.dateKey === todayStr;
            const isSelected = selectedDate === cell.dateKey;
            return (
              <div
                key={key}
                className={`cc-month-cell ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedDate(cell.dateKey)}
              >
                <div className={`cc-month-day ${isToday ? 'cc-month-day-today' : ''}`}>{cell.day}</div>
                {cell.events && cell.events.length > 0 && (
                  <ul className="cc-month-events">
                    {cell.events.slice(0, 3).map(ev => (
                      <li
                        key={ev.id}
                        className="cc-month-event-title"
                        onMouseEnter={(e) => handleEventHoverIn(ev, e)}
                        onMouseMove={(e) => handleEventHoverMove(ev, e)}
                        onMouseLeave={handleEventHoverOut}
                      >
                        {(ev.createdById && currentUser.id && ev.createdById === currentUser.id) || currentUser.isAdmin ? (
                          <button
                            type="button"
                            className="mini-btn cc-month-pencil"
                            onClick={() => openEditFromMonth(ev)}
                            title="Edit event"
                          >
                            <FaEdit size={16} />
                          </button>
                        ) : null}
                        {ev.time && <span className="cc-month-event-time">{ev.time}</span>}
                        {ev.title}
                        {ev.createdBy && ` (created by ${ev.createdBy})`}
                      </li>
                    ))}
                    {cell.events.length > 3 && (
                      <li className="cc-month-event-more">
                        +{cell.events.length - 3} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div className="company-calendar">
      <header className="cc-header">
        <div className="cc-title">
          <FaCalendarAlt size={20} />
          <h1>Company Calendar</h1>
        </div>
        <p className="cc-subtitle">
          Internal-only calendar for company events, deadlines, and notes.
        </p>
        {error && (
          <div className="cc-error">
            {error}
          </div>
        )}
      </header>

      <section className="cc-layout">
        <form className="cc-form" onSubmit={addEvent}>
          <h2>
            <FaPlus style={{ marginRight: 6 }} />
            New Event
          </h2>
          <label className="cc-field">
            <span>Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
          <label className="cc-field">
            <span>Time (optional)</span>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
          <label className="cc-field">
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., System maintenance, Client visit, Warehouse audit"
              required
            />
          </label>
          <label className="cc-field">
            <span>Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any details, agendas, or follow-up items…"
              rows={4}
            />
          </label>
          <button type="submit" className="btn primary">
            <FaPlus style={{ marginRight: 6 }} />
            Add Event
          </button>
        </form>

        <div className="cc-events">
          <div className="cc-events-header">
            <div className="cc-events-title">
              <FaStickyNote style={{ marginRight: 6 }} />
              <h2>Events</h2>
            </div>
            <div className="cc-field-inline">
              <div className="cc-month-input-wrap">
                <div className="cc-year-nav">
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={() => changeYearBy(-1)}
                    title="Previous year"
                    aria-label="Previous year"
                  >
                    <FaChevronLeft size={12} />
                  </button>
                  <span className="cc-year-label">{currentYear}</span>
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={() => changeYearBy(1)}
                    title="Next year"
                    aria-label="Next year"
                  >
                    <FaChevronRight size={12} />
                  </button>
                </div>
                <div className="cc-month-nav">
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={() => changeMonthBy(-1)}
                    title="Previous month"
                    aria-label="Previous month"
                  >
                    <FaChevronLeft size={12} />
                  </button>
                  <span className="cc-month-label">{currentMonthName}</span>
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={() => changeMonthBy(1)}
                    title="Next month"
                    aria-label="Next month"
                  >
                    <FaChevronRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="cc-view-toggle">
            <button
              type="button"
              className={`mini-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month view
            </button>
            <button
              type="button"
              className={`mini-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List view
            </button>
          </div>

          {viewMode === 'month' ? (
            <Split
              className="cc-month-split"
              direction="vertical"
              sizes={monthSplitSizes}
              minSize={[200, 120]}
              gutterSize={6}
              style={{ height: '100%', width: '100%' }}
              onDragEnd={(sizes) => {
                setMonthSplitSizes(sizes);
                try {
                  localStorage.setItem('calendar.monthSplitSizes', JSON.stringify(sizes));
                } catch {}
              }}
            >
              <div className="cc-month-wrapper">
                {renderMonthGrid()}
              </div>
              <div className="cc-day-detail">
                <div className="cc-day-header">
                  <span className="cc-day-label">
                    {selectedDate
                      ? new Date(selectedDate + 'T00:00:00').toLocaleDateString()
                      : 'No day selected'}
                  </span>
                  <span className="cc-day-count">
                    {selectedEvents.length} event{selectedEvents.length === 1 ? '' : 's'}
                  </span>
                </div>
                {selectedEvents.length === 0 ? (
                  <div className="cc-empty-inline">
                    No events for this day.
                  </div>
                ) : (
                  <ul className="cc-day-events">
                    {selectedEvents.map(ev => (
                      <li
                        key={ev.id}
                        className="cc-event"
                        onMouseEnter={(e) => handleEventHoverIn(ev, e)}
                        onMouseMove={(e) => handleEventHoverMove(ev, e)}
                        onMouseLeave={handleEventHoverOut}
                      >
                        <div className="cc-event-main">
                          {editingId === ev.id ? (
                            <>
                              <div className="cc-event-title-row">
                                <input
                                  className="cc-edit-title"
                                  type="text"
                                  value={editTitle}
                                  onChange={e => setEditTitle(e.target.value)}
                                />
                              </div>
                              <div className="cc-event-edit-date">
                                <input
                                  type="date"
                                  value={editDate}
                                  onChange={e => setEditDate(e.target.value)}
                                />
                                <input
                                  type="time"
                                  value={editTime}
                                  onChange={e => setEditTime(e.target.value)}
                                />
                              </div>
                              <div className="cc-event-notes">
                                <textarea
                                  className="cc-edit-notes"
                                  rows={3}
                                  value={editNotes}
                                  onChange={e => setEditNotes(e.target.value)}
                                />
                              </div>
                              <div className="cc-event-edit-actions">
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => saveEdit(ev.id)}
                                  disabled={loading}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={cancelEdit}
                                  disabled={loading}
                                >
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="cc-event-title-row">
                                <div className="cc-event-title-wrap">
                                  {(ev.createdById && currentUser.id && ev.createdById === currentUser.id) || currentUser.isAdmin ? (
                                    <button
                                      type="button"
                                      className="mini-btn cc-pencil-btn"
                                      onClick={() => beginEdit(ev)}
                                      title="Edit event"
                                    >
                                      <FaEdit size={16} />
                                    </button>
                                  ) : null}
                                  <strong className="cc-event-title">
                                    {ev.time && `${ev.time} – `}{ev.title}
                                    {ev.createdBy && ` (created by ${ev.createdBy})`}
                                  </strong>
                                </div>
                                {(ev.createdById && currentUser.id && ev.createdById === currentUser.id) || currentUser.isAdmin ? (
                                  <button
                                    type="button"
                                    className="mini-btn danger"
                                    onClick={() => deleteEvent(ev.id)}
                                    title="Delete event"
                                    disabled={loading}
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                ) : null}
                              </div>
                              {ev.notes && (
                                <div className="cc-event-notes">
                                  {ev.notes}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="cc-event-meta">
                          {ev.createdBy && <span>By {ev.createdBy}</span>}
                          {ev.createdAt && (
                            <span>
                              Created {new Date(ev.createdAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Split>
          ) : groupedByDate.length === 0 ? (
            <div className="cc-empty">
              No events for this month yet. Create one on the left.
            </div>
          ) : (
            <div className="cc-events-list">
              {groupedByDate.map(([day, list]) => (
                <div key={day} className="cc-day-block">
                  <div className="cc-day-header">
                    <span className="cc-day-label">
                      {new Date(day + 'T00:00:00').toLocaleDateString()}
                    </span>
                    <span className="cc-day-count">
                      {list.length} event{list.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <ul className="cc-day-events">
                    {list
                      .slice()
                      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
                      .map(ev => (
                        <li
                          key={ev.id}
                          className="cc-event"
                          onMouseEnter={(e) => handleEventHoverIn(ev, e)}
                          onMouseMove={(e) => handleEventHoverMove(ev, e)}
                          onMouseLeave={handleEventHoverOut}
                        >
                          <div className="cc-event-main">
                            {editingId === ev.id ? (
                              <>
                                <div className="cc-event-title-row">
                                  <input
                                    className="cc-edit-title"
                                    type="text"
                                    value={editTitle}
                                    onChange={e => setEditTitle(e.target.value)}
                                  />
                                </div>
                                <div className="cc-event-edit-date">
                                  <input
                                    type="date"
                                    value={editDate}
                                    onChange={e => setEditDate(e.target.value)}
                                  />
                                  <input
                                    type="time"
                                    value={editTime}
                                    onChange={e => setEditTime(e.target.value)}
                                  />
                                </div>
                                <div className="cc-event-notes">
                                  <textarea
                                    className="cc-edit-notes"
                                    rows={3}
                                    value={editNotes}
                                    onChange={e => setEditNotes(e.target.value)}
                                  />
                                </div>
                                <div className="cc-event-edit-actions">
                                  <button
                                    type="button"
                                    className="btn"
                                    onClick={() => saveEdit(ev.id)}
                                    disabled={loading}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="btn"
                                    onClick={cancelEdit}
                                    disabled={loading}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="cc-event-title-row">
                                  <div className="cc-event-title-wrap">
                                    {ev.createdById && currentUser.id && ev.createdById === currentUser.id && (
                                      <button
                                        type="button"
                                        className="mini-btn cc-pencil-btn"
                                        onClick={() => beginEdit(ev)}
                                        title="Edit event"
                                      >
                                        <FaEdit size={16} />
                                      </button>
                                    )}
                                    <strong className="cc-event-title">
                                      {ev.time && `${ev.time} – `}{ev.title}
                                      {ev.createdBy && ` (created by ${ev.createdBy})`}
                                    </strong>
                                  </div>
                                  {(ev.createdById && currentUser.id && ev.createdById === currentUser.id) || currentUser.isAdmin ? (
                                    <button
                                      type="button"
                                      className="mini-btn danger"
                                      onClick={() => deleteEvent(ev.id)}
                                      title="Delete event"
                                      disabled={loading}
                                    >
                                      <FaTrash size={12} />
                                    </button>
                                  ) : null}
                                </div>
                                {ev.notes && (
                                  <div className="cc-event-notes">
                                    {ev.notes}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="cc-event-meta">
                            {ev.createdBy && <span>By {ev.createdBy}</span>}
                            {ev.createdAt && (
                              <span>
                                Created {new Date(ev.createdAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      {hoveredEvent && (
        <div
          className="cc-event-tooltip"
          style={{ top: tooltipPos.y, left: tooltipPos.x }}
        >
          <div className="cc-event-tooltip-title">
            {hoveredEvent.title || '(No title)'}
          </div>
          <div className="cc-event-tooltip-row">
            <span className="cc-event-tooltip-label">Date:</span>
            <span>
              {hoveredEvent.date
                ? new Date(`${hoveredEvent.date}T00:00:00`).toLocaleDateString()
                : 'N/A'}
            </span>
          </div>
          {hoveredEvent.time && (
            <div className="cc-event-tooltip-row">
              <span className="cc-event-tooltip-label">Time:</span>
              <span>{hoveredEvent.time}</span>
            </div>
          )}
          {hoveredEvent.createdBy && (
            <div className="cc-event-tooltip-row">
              <span className="cc-event-tooltip-label">Created by:</span>
              <span>{hoveredEvent.createdBy}</span>
            </div>
          )}
          {hoveredEvent.createdAt && (
            <div className="cc-event-tooltip-row">
              <span className="cc-event-tooltip-label">Created at:</span>
              <span>
                {new Date(hoveredEvent.createdAt).toLocaleString()}
              </span>
            </div>
          )}
          {hoveredEvent.notes && (
            <div className="cc-event-tooltip-notes">
              {hoveredEvent.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
